import moment from 'moment';

// A TARGET's ACTIVE baseline drawn as its own phase: one point per baseline session on the left, a phase-change
// line, then the report range. The baseline is not tied to the report dates.
export const PHASE_CHANGE_KEY = '__phase_change__';

const PHASE_KEY_PREFIX = '__baseline_';
const BASELINE_COLOR = '#101828';
const PHASE_CHANGE_COLOR = '#98A2B3';
// The phase takes at least this share of the axis, so a short baseline next to a long (e.g. daily) range is not
// squeezed into a sliver. Its points are spread evenly over that width.
const MIN_PHASE_SHARE = 0.2;

/**
 * `points` = [{ date: 'YYYY-MM-DD', value }], oldest first; `rangeLength` = number of range categories.
 * Returns the leading axis keys (points, spacers, phase change), their values and label helpers.
 */
export const getBaselinePhaseLayout = (points = [], rangeLength = 0) => {
    const phase = (points || []).filter((point) => point?.date && point?.value !== null && point?.value !== undefined);
    const minSlots = Math.ceil((rangeLength * MIN_PHASE_SHARE) / (1 - MIN_PHASE_SHARE));
    const slotsPerPoint = phase.length ? Math.max(1, Math.floor(minSlots / phase.length)) : 1;
    const keys = [];
    const values = [];
    const dateByKey = {};
    phase.forEach((point, index) => {
        const key = `${PHASE_KEY_PREFIX}${index}`;
        keys.push(key);
        values.push(point.value);
        dateByKey[key] = point.date;
        for (let slot = 1; slot < slotsPerPoint; slot++) {
            keys.push(`${key}_${slot}`);
            values.push(null);
        }
    });
    const leading = keys.length ? [...keys, PHASE_CHANGE_KEY] : [];
    return {
        phase,
        keys,
        values,
        leading,
        /** Shifts range-aligned data right, past the baseline phase. */
        pad: (rangeValues = []) => (leading.length ? [...leading.map(() => null), ...rangeValues] : rangeValues),
        /** Label for a phase key ('' for spacers and the phase change); `null` for a range category. */
        formatKey: (key, long = false) => {
            if (key === PHASE_CHANGE_KEY) return '';
            if (typeof key !== 'string' || !key.startsWith(PHASE_KEY_PREFIX)) return null;
            const date = dateByKey[key];
            return date ? moment(date, 'YYYY-MM-DD').format(long ? 'DD MMM YYYY' : 'DD MMM') : '';
        },
    };
};

/** The baseline line, with a light band behind the phase. */
export const buildBaselinePhaseSeries = (layout, rangeLength) =>
    layout.phase.length
        ? [
              {
                  name: 'Baseline',
                  type: 'line',
                  connectNulls: true,
                  data: [...layout.values, null, ...Array(rangeLength).fill(null)],
                  symbol: 'triangle',
                  symbolSize: 7,
                  // Every baseline point, even on a dense axis (ECharts' 'auto' would hide some).
                  showAllSymbol: true,
                  lineStyle: { color: BASELINE_COLOR, width: 2 },
                  itemStyle: { color: BASELINE_COLOR },
                  z: 2,
                  markArea: {
                      silent: true,
                      itemStyle: { color: 'rgba(16, 24, 40, 0.04)' },
                      label: { show: true, position: 'insideTop', formatter: 'Baseline', color: '#667085', fontSize: 10 },
                      data: [[{ xAxis: layout.keys[0] }, { xAxis: PHASE_CHANGE_KEY }]],
                  },
              },
          ]
        : [];

/** The phase-change divider, added to the chart's mark lines. */
export const buildPhaseChangeMarkLine = () => ({
    xAxis: PHASE_CHANGE_KEY,
    label: { show: false },
    lineStyle: { color: PHASE_CHANGE_COLOR, type: [4, 3], width: 1.5 },
    tooltip: { show: false },
});
